import {
  Box,
  Button,
  Checkbox,
  Flex,
  Image,
  Spinner,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Account, QuoteStatus, RequireOnly } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { useProvider } from '../hooks/providers/useProvider';
import { useQuote } from '../hooks/quote/useQuote';
import { getBorderColor, getErrorMessage, useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { quoteStatusColorScheme } from '../utils/constants';
import { LabeledText } from './LabeledText';
import {
  SimpleContactSearch,
  SimpleContactSearchForm,
  SimpleContactSearchProps,
  SimpleContactSearchSelected,
} from './SimpleContactSearch';

type SimpleAirlineRecipientFormProps = Omit<SimpleContactSearchProps, 'name' | 'children' | 'refreshContactSearch'> & {
  defaultAccount?: RequireOnly<Account, 'id' | 'name'>;
  prefix: string;
  airportId?: string;
};

export const SimpleAirlineRecipientForm = ({
  prefix,
  defaultAccount,
  ...contactSearchProps
}: SimpleAirlineRecipientFormProps) => {
  const { id, isExpanded, originAirportId, recipients, quoteId } = useWatch({
    name: prefix,
  });
  const hasNonAirlineRecipients = useMemo(() => {
    return recipients?.some((recipient) => recipient?.provider_id !== id);
  }, [recipients, id]);

  const { error = {}, isInvalid } = useFieldValidationStates(prefix as string);
  const errorMessages = getErrorMessage(error);

  const borderColor = getBorderColor({
    hasError: isInvalid,
    hasWarning: hasNonAirlineRecipients,
    isExpanded,
  });
  const searchDisclosure = useDisclosure({ defaultIsOpen: true });

  const scrollToRef = useRef<HTMLDivElement>();

  const scrollSearchIntoView = () => {
    if (scrollToRef.current) {
      (scrollToRef.current as any)?.scrollIntoViewIfNeeded({
        behavior: 'smooth',
      });
    }
  };

  const { setValue } = useFormContext();
  const [airline, isAirlineLoading] = useProvider(id);
  const [quote, isQuoteLoading] = useQuote(quoteId);
  const isSentToCarrier = quote?.status === QuoteStatus.SentToCarrier;

  const toggledIsExpanded = () => {
    if (isSentToCarrier) return;
    setValue(`${prefix}.isExpanded`, !isExpanded);
  };
  const toggleShowSearch = () => {
    searchDisclosure.onToggle();
    if (!searchDisclosure.isOpen) setTimeout(scrollSearchIntoView, 100);
  };

  useEffect(() => {
    if (isSentToCarrier && isExpanded) {
      setValue(`${prefix}.isExpanded`, false);
    }
  }, [isSentToCarrier, isExpanded, prefix, setValue]);

  const { data: fulfillmentPartner } = trpc.getFulfillmentPartner.useQuery(
    { id: quote?.fulfillment_partner_configuration_id },
    {
      enabled: !!quote?.fulfillment_partner_configuration_id,
      refetchOnWindowFocus: false,
    },
  );

  const bgColorHover = useColorModeValue('gray.50', 'gray.600');
  const colorScheme = useMemo(
    () => (quote?.status ? quoteStatusColorScheme[quote.status] : undefined),
    [quote?.status],
  );

  if (isAirlineLoading) {
    return (
      <Stack borderTopWidth={1} direction="row" justifyItems="center" alignItems="center" py={6} w="full">
        <Spinner size="md" />;
      </Stack>
    );
  }

  return (
    <Flex mb={2} key={airline?.id} direction="column" borderWidth={1} borderColor={borderColor} borderRadius="lg">
      <SimpleContactSearch
        name={`${prefix}.recipients`}
        {...contactSearchProps}
        defaultAccounts={[defaultAccount]}
        defaultAirportIds={[originAirportId]}
        defaultProviderIds={[id]}
        searchPattern="AIRLINE"
      >
        <Stack
          direction="row"
          alignItems="center"
          justify="space-between"
          py={6}
          px={4}
          spacing={4}
          w="full"
          onClick={toggledIsExpanded}
          borderRadius="lg"
          _hover={isSentToCarrier ? undefined : { bgColor: bgColorHover }}
          cursor={isSentToCarrier ? 'not-allowed' : undefined}
          opacity={isSentToCarrier ? 0.7 : undefined}
          userSelect="none"
        >
          <Stack direction="row" spacing="4" align="center">
            <Checkbox
              colorScheme="brand"
              size="lg"
              isChecked={isExpanded}
              isDisabled={isSentToCarrier}
              pointerEvents="none"
            />
            <Image src={airline?.logo_uri} w={30} alt={`${airline?.name}'s logo`} />
            <Text>{airline?.name}</Text>
          </Stack>
          <Stack spacing={0} justify="center">
            <Show if={!!error}>
              <Text color="red.500" fontSize="xs">
                {errorMessages}
              </Text>
            </Show>
            <Show if={isSentToCarrier}>
              <Text color="gray.500" fontSize="xs">
                Already sent to carrier — change quote status to send again.
              </Text>
            </Show>
            <Show if={hasNonAirlineRecipients}>
              <Text color="orange.500" fontSize="xs">
                Caution! Recipients from other airlines are included.
              </Text>
            </Show>
          </Stack>
          <Show if={isQuoteLoading}>
            <Spinner size="sm" />
          </Show>
          <Show if={!!fulfillmentPartner}>
            <LabeledText
              value={fulfillmentPartner?.account_as_fulfillment_partner?.name}
              textProps={{ noOfLines: 1 }}
            />
          </Show>
          <Show if={!isQuoteLoading}>
            <Tag colorScheme={colorScheme} size="sm" borderRadius="md">
              {quote?.status}
            </Tag>
          </Show>
        </Stack>
        <Stack p={4} pt={1} hidden={!isExpanded}>
          <SimpleContactSearchSelected />

          <Button
            variant="ghost"
            colorScheme="brand"
            size="sm"
            rightIcon={searchDisclosure.isOpen ? <HiChevronUp /> : <HiChevronDown />}
            onClick={toggleShowSearch}
          >
            {searchDisclosure.isOpen ? 'Minimize Search' : 'Expand Search'}
          </Button>

          <SimpleContactSearchForm borderWidth={1} p={3} borderRadius="md" hidden={!searchDisclosure.isOpen} />
          <Box ref={scrollToRef} />
        </Stack>
      </SimpleContactSearch>
    </Flex>
  );
};
